/* eslint-disable camelcase */
import * as THREE from 'three'
import { getLineMaterial, getSeatMaterial } from '../utils/helper_functions'
import { drawLabel } from './drawLabel'
import { COLORS, SEAT_TYPES, SEAT_STATUSES, OBJECT_TYPES } from '../constants'
import { textGeometries, materials } from '../utils/mapData'

export const drawSeat = (seatId, options) => {
  let {
    color,
    position,
    rotation,
    tierId,
    parent,
    center_x,
    center_y,
    scale,
    seat_circle_radius,
    events,
    sectionLabel,
    rowLabel,
    label,
    fonts,
    opacity = 1,
    disabled = false,
    type = SEAT_TYPES.CHAIR,
    seats,
    tierColor,
    enableTicketTypeAssign,
    selectedTierId,
    sceneMeshes
  } = options
  const seatColor = enableTicketTypeAssign ? tierColor : COLORS.BOOKED
  position[0] -= center_x * scale
  position[1] += center_y * scale

  const radius = (seat_circle_radius || 14) * scale
  const geometry = new THREE.CircleGeometry(radius, 32)

  if (selectedTierId && selectedTierId !== tierId) {
    opacity = 0.3
  }

  const circle = new THREE.Mesh(
    geometry,
    getSeatMaterial({ color: seatColor, opacity }, materials)
  )
  circle.position.set(...position)
  circle.isRequestPending = false

  const edges = new THREE.EdgesGeometry(geometry)
  const line = new THREE.LineSegments(
    edges,
    getLineMaterial({ color: seatColor, opacity }, materials)
  )
  line.position.set(...position)
  parent.add(line)
  parent.add(circle)

  parent.add(circle)

  if (type === SEAT_TYPES.WHEELCHAIR) {
    drawLabel(
      '\uF193',
      {
        font: fonts.fontAwesome,
        color: 'black',
        position: [0, 0, 0],
        center_x: 0,
        center_y: 0,
        scale,
        seat_circle_radius,
        scene: circle,
        size: seat_circle_radius * scale,
        rotation
      },
      textGeometries,
      materials
    )
  }

  circle.objectType = OBJECT_TYPES.SEAT
  circle.opacity = opacity
  circle.disabled = disabled
  circle.allowColorUpdateByStatus = true
  circle.radius = radius
  circle.seatId = seatId
  circle.tierId = tierId
  circle.status = SEAT_STATUSES.BOOKED
  circle.sectionLabel = sectionLabel
  circle.rowLabel = rowLabel
  circle.label = label
  circle.seatType = type
  circle.changeBorderColor = (color) => {
    if (!enableTicketTypeAssign) {
      line.material = getLineMaterial(
        { color, opacity: circle.opacity },
        materials
      )
    }
  }

  circle.mouseOver = circle.disabled ? null : events.onMouseOver
  circle.mouseOut = circle.disabled ? null : events.onMouseOut
  circle.changeBorderColorToDefault = () =>
    circle.changeBorderColor(COLORS.BOOKED)

  circle.changeColor = (color) => {
    line.material = getLineMaterial(
      { color, opacity: circle.opacity },
      materials
    )
    circle.material = getSeatMaterial(
      { color, opacity: circle.opacity },
      materials
    )
    circle.changeBorderColorToDefault = () => circle.changeBorderColor(color)
    circle.color = color
  }

  circle.changeOpacity = (opacity, color = seatColor) => {
    line.material = getLineMaterial({ color, opacity }, materials)
    circle.material = getSeatMaterial({ color, opacity }, materials)
  }

  const makeAvailable = () => {
    circle.status = SEAT_STATUSES.AVAILABLE
  }

  const makeHold = () => {
    circle.status = SEAT_STATUSES.HOLD
  }

  const makeReserved = () => {
    circle.status = SEAT_STATUSES.RESERVED
  }

  const makeBooked = () => {
    circle.status = SEAT_STATUSES.BOOKED
  }

  const makeBlocked = () => {
    circle.status = SEAT_STATUSES.BLOCKED
  }

  const makeReservedByMe = () => {
    window.localStorage.setItem('tierId', tierId)
    circle.status = SEAT_STATUSES.RESERVED_BY_ME
  }

  circle.setColorByStatus = () => {
    if (!circle.allowColorUpdateByStatus) {
      return
    }

    switch (circle.status) {
      case SEAT_STATUSES.AVAILABLE: {
        circle.changeColor(color)
        break
      }
      case SEAT_STATUSES.HOLD: {
        circle.changeColor(COLORS.HOLD)
        break
      }
      case SEAT_STATUSES.RESERVED_BY_ME: {
        circle.changeColor(COLORS.RESERVED_BY_ME)
        break
      }
      case SEAT_STATUSES.RESERVED: {
        circle.changeColor(COLORS.RESERVED)
        break
      }
      case SEAT_STATUSES.BOOKED: {
        circle.changeColor(COLORS.BOOKED)
        break
      }
      case SEAT_STATUSES.BLOCKED: {
        circle.changeColor(COLORS.BLOCKED)
        break
      }
    }
  }

  circle.updateOpacity = () => {
    circle.changeColor(`#${circle.material.color.getHexString()}`)
  }

  circle.makeDisabled = () => {
    if (circle.disabled) {
      return
    }
    circle.disabled = true
    circle.mouseOver = null
    circle.mouseOut = null
    circle.opacity = 0.3
    circle.updateOpacity()
  }

  circle.makeActive = () => {
    if (!circle.disabled) {
      return
    }
    circle.disabled = false
    circle.mouseOver = events.onMouseOver
    circle.mouseOut = events.onMouseOut
    circle.opacity = 1
    circle.updateOpacity()
  }

  const select = () => {
    line.material = getLineMaterial(
      {
        color: '#575656',
        linewidth: 5,
        opacity: circle.opacity
      },
      materials
    )
    circle.changeBorderColor = () => null
  }

  const unselect = () => {
    line.material = getLineMaterial(
      { color: '#000', opacity: circle.opacity },
      materials
    )
    circle.changeBorderColor = (color) => {
      line.material = getLineMaterial(
        { color, opacity: circle.opacity },
        materials
      )
    }
    circle.changeBorderColorToDefault()
  }

  circle.select = select
  circle.unselect = unselect

  circle.unsetLoading = () => null
  circle.setLoading = () => {
    if (window.isMobile) {
      // is mobile
      const label = drawLabel(
        '\uF110',
        {
          font: fonts.fontAwesome,
          color: 'grey',
          position: [0, 0, 0],
          center_x: 0,
          center_y: 0,
          scale,
          seat_circle_radius,
          scene: circle,
          size: seat_circle_radius * scale,
          rotation
        },
        textGeometries,
        materials
      )
      circle.unsetLoading = () => circle.remove(label)
    }
  }

  circle.statusUpdate = (status) => {
    if (circle.status !== status) {
      switch (status) {
        case SEAT_STATUSES.PERMANENT_RESERVED:
        case SEAT_STATUSES.AVAILABLE: {
          makeAvailable()
          circle.unsetLoading()
          break
        }
        case SEAT_STATUSES.HOLD: {
          makeHold()
          circle.unsetLoading()
          break
        }
        case SEAT_STATUSES.RESERVED_BY_ME: {
          makeReservedByMe()
          circle.unsetLoading()
          break
        }
        case SEAT_STATUSES.RESERVED: {
          makeReserved()
          circle.unsetLoading()
          break
        }
        case SEAT_STATUSES.BOOKED: {
          makeBooked()
          circle.unsetLoading()
          break
        }
        case SEAT_STATUSES.BLOCKED: {
          makeBlocked()
          circle.unsetLoading()
          break
        }
      }

      circle.setColorByStatus()
    }
  }
  circle.hasHandlers = true
  sceneMeshes.push(circle)
  seats[seatId] = circle
}
